import { useFieldArray, useFormContext } from 'react-hook-form'
import {
  MeasuresAppliedDefaultValues,
  MeasuresAppliedType,
  SocioeducationMeasureMeasuresAppliedType,
} from 'types/socioeducation-measure-measures-applied.type'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  DialogProps,
  Grid,
  IconButton,
  Pagination,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import HistoryIcon from '@mui/icons-material/History'
import { MeasuresAppliedItem } from 'pages/SocioeducationMeasureEdit/MeasuresApplied/components/MeasuresAppliedItem'
import { useParams } from 'react-router-dom'
import { useMeioAbertoHistorico } from 'hooks/meio-aberto-historico.hook'
import DialogTitle from '@mui/material/DialogTitle'
import CloseIcon from '@mui/icons-material/Close'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import dayjs from 'dayjs'
import { medidaAplicadaOptions } from 'pages/SocioeducationMeasureEdit/ProceduralData'
import { styled } from '@mui/material/styles'
import { ViewFile } from 'components/FormInputFile'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'

const StyledCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '1rem',
  width: '100%',
  color: theme.palette.text.primary,
  borderRadius: '0.5rem',
  border: '1px solid',
  borderColor: theme.palette.primary.main,
}))

interface LabelProps {
  label: string
  value?: string | number | null
}

const Label = ({ label, value }: LabelProps) => (
  <Box display={'flex'} flexDirection={'column'}>
    <Typography variant={'body2'}>
      <b>{label}</b>
    </Typography>
    <Typography variant={'body2'}>{value}</Typography>
  </Box>
)

interface MeasuresAppliedListProps {
  disabled?: boolean
}

export function MeasuresAppliedList({ disabled }: MeasuresAppliedListProps) {
  const { control, setValue } = useFormContext<SocioeducationMeasureMeasuresAppliedType>()

  const { fields, append, remove } = useFieldArray<SocioeducationMeasureMeasuresAppliedType>({
    control,
    name: 'medidasAplicadas',
  })

  useEffect(() => {
    if (fields.length === 0) {
      setValue('medidasAplicadas', [MeasuresAppliedDefaultValues])
    }
  }, [fields])

  const handleNew = () => {
    append(MeasuresAppliedDefaultValues)
  }

  const shouldShowHistory = (fields: MeasuresAppliedType[]) => {
    if (fields.length === 0) return false

    const firstItem = fields[0]

    return firstItem.meioAberto?.status === 'ALTERADO'
  }

  return (
    <Grid container item spacing={2}>
      {fields.map((field, index) => (
        <Grid item xs={12} key={field.id}>
          <MeasuresAppliedItem index={index} remove={remove} disabled={disabled} />
        </Grid>
      ))}

      <Grid item xs={12}>
        <Box display={'flex'} gap={2}>
          <Button
            variant={'contained'}
            color={'secondary'}
            startIcon={<AddIcon />}
            onClick={handleNew}
            disabled={disabled}
          >
            MEDIDAS APLICADAS
          </Button>
          {shouldShowHistory(fields as MeasuresAppliedType[]) && <HistoryMeasuresApplied />}
        </Box>
      </Grid>
    </Grid>
  )
}

const HistoryMeasuresApplied = () => {
  const { id } = useParams()
  const [fullWidth] = useState(true)
  const [maxWidth] = useState<DialogProps['maxWidth']>('md')

  const [open, setOpen] = useState<boolean>(false)

  const {
    historico,
    isLoading,
    page,
    totalOfElements,
    totalOfPages,
    handleNewPage,
    rowsPerPage,
    rowsOptions,
    handleRowsPerPage,
  } = useMeioAbertoHistorico(id)

  const somatoriaItens = useMemo(
    () => page * rowsPerPage + (historico?.length || 0),
    [page, rowsPerPage, historico],
  )

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const getMedidaAplicada = (medidaAplicada?: string | null) => {
    if (medidaAplicada) {
      return medidaAplicadaOptions.find((item) => item.value === medidaAplicada)?.label
    }

    return ''
  }

  return (
    <Fragment>
      <Button variant={'contained'} color={'info'} startIcon={<HistoryIcon />} onClick={handleOpen}>
        HISTÓRICO DE MEDIDAS APLICADAS
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth={fullWidth} maxWidth={maxWidth}>
        <DialogTitle>
          <Typography variant={'h6'} color={'primary.main'}>
            HISTÓRICO DE MEDIDAS APLICADAS
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Grid container spacing={2}>
            {!isLoading && historico ? (
              historico.map((item) => (
                <Grid item xs={12} key={item.id}>
                  <StyledCard>
                    <Box
                      display={'flex'}
                      flexDirection={'row'}
                      justifyContent={'space-between'}
                      alignItems={'center'}
                      marginBottom={'1rem'}
                    >
                      <Typography variant={'h6'}>
                        <b>{getMedidaAplicada(item.dadosProcessuais?.tipoMedidaSocioeducativa)}</b>
                      </Typography>
                      <b>{dayjs(item.dataRelatorio).format('DD/MM/YYYY HH:mm')}</b>
                    </Box>
                    {item.medidaAplicada?.medidasAplicadas.map((medida) => (
                      <Box
                        display={'flex'}
                        flexDirection={'row'}
                        alignItems={'center'}
                        gap={3}
                        flexWrap={'wrap'}
                        key={medida.ordem}
                      >
                        {item.medidaAplicada?.la.ativo && (
                          <Label
                            label={'Data Início do Cumprimento da LA'}
                            value={dayjs(medida.la.dataInicioCumprimento).format('DD/MM/YYYY')}
                          />
                        )}
                        {item.medidaAplicada?.psc.ativo && (
                          <Label
                            label={'Data Início do Cumprimento da PSC'}
                            value={
                              medida.psc.dataInicioCumprimento &&
                              dayjs(medida.psc.dataInicioCumprimento).format('DD/MM/YYYY')
                            }
                          />
                        )}
                        <Label
                          label={'Data de Entrega do Relatório'}
                          value={dayjs(medida.dataEntregaRelatorio).format('DD/MM/YYYY')}
                        />
                        {medida.anexo && medida.anexo?.objetoAnexo && (
                          <ViewFile
                            nomeArquivo={medida.anexo?.tipoArquivo}
                            arquivo={medida.anexo?.objetoAnexo}
                            tipoArquivo={medida.anexo?.nomeArquivo}
                          />
                        )}
                      </Box>
                    ))}
                  </StyledCard>
                </Grid>
              ))
            ) : (
              <Grid
                item
                xs={12}
                display={'flex'}
                flexDirection={'row'}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <CircularProgress />
              </Grid>
            )}
            <Grid item xs={12}>
              <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                <Typography variant={'body2'}>
                  Mostrando {somatoriaItens} de {totalOfElements || 0}
                </Typography>
                <Pagination
                  count={totalOfPages}
                  page={page + 1}
                  variant='outlined'
                  shape='rounded'
                  onChange={(e, value) => handleNewPage(null, value - 1)}
                />
                <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
                  <Typography variant={'body2'} sx={{ marginRight: '0.25rem' }}>
                    Itens por página:
                  </Typography>
                  <FormControl sx={{ m: 1, minWidth: 120 }} size='small'>
                    <Select
                      labelId='demo-select-small-label'
                      variant={'outlined'}
                      id='demo-select-small'
                      value={rowsPerPage}
                      onChange={(e: any) => handleRowsPerPage(e)}
                    >
                      {rowsOptions.map((item, index) => (
                        <MenuItem key={index} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Fragment>
  )
}
